/**
 * 3 starter prompts per Sourcing AI agent (SS-4.1 → SS-4.16).
 * Rendered as clickable chips in <SourcingAgentPanel> to lower the cold-start
 * cost for first-time users and showcase each agent's depth.
 */
export const AGENT_STARTERS = {
  ss41: [
    'Recommend an RFP evaluation methodology for a $5M services category.',
    'Build a Kraljic-driven sourcing strategy for IT-infra spend.',
    'Draft a stakeholder approval ladder using value bands.',
  ],
  ss42: [
    'Identify the top 10 suppliers in Asia for industrial pumps.',
    'Score this supplier long-list for diversity & risk.',
    'Draft an invitation email template with NDA included.',
  ],
  ss43: [
    'Cluster overlapping bidder questions into a single addendum.',
    'Suggest a fair response window for technical clarifications.',
    'Spot ambiguities in this RFP that will trigger Q&A.',
  ],
  ss44: [
    'Validate this proposal for completeness against the checklist.',
    'Flag any missing certifications in this bid package.',
    'Detect late submissions and recommend the penalty action.',
  ],
  ss45: [
    'Reconstruct the bid-opening audit trail for event EVT-001.',
    'Spot pricing anomalies across this bid set.',
    'Generate a public opening minutes record.',
  ],
  ss46: [
    'Normalise prices across bidders with different inclusions.',
    'Compute MEAT score with weights 70/30 (Tech/Cost).',
    'Spot evaluator bias in scoring patterns.',
  ],
  ss47: [
    'Design an English reverse auction strategy with floor + ceiling.',
    'Should I run a sealed-bid eAuction or open reverse?',
    'Recommend bid increments for a 2-hour auction.',
  ],
  ss48: [
    'Draft a BAFO invitation that pressure-tests the top 3 bidders.',
    'Choose the right BAFO mode (price-only vs full bid).',
    'Build a counter-offer matrix for negotiation.',
  ],
  ss49: [
    'When should I use a single-supplier vs multi-supplier framework?',
    'Draft a framework call-off mini-competition workflow.',
    'Audit framework utilisation by category.',
  ],
  ss410: [
    'Justify a direct award using the regulation exemption clause.',
    'Build a DA approval pack with all required evidence.',
    'When does aggregation-splitting become non-compliant?',
  ],
  ss411: [
    'Generate the award recommendation memo from this scorecard.',
    'Detect any tie-break ambiguity in the evaluation outcome.',
    'Compare two finalists with rationale + risk flags.',
  ],
  ss412: [
    'Draft a polite regret letter that respects standstill.',
    'Summarise validated savings for the closure pack.',
    'Build the audit-ready closure archive checklist.',
  ],
  // Cross-cutting agents
  ss413: [
    'What cadence should I use for deadline reminders?',
    'Diagnose why my delivery rate dropped below 95 %.',
    'Stagger 800 invitation emails over 4 hours without ESP throttling.',
  ],
  ss414: [
    'Why is my sourcing cycle time slipping?',
    'Top 3 categories driving validated savings this quarter.',
    'Build an audit-ready savings export for finance.',
  ],
  ss415: [
    'Audit my supplier portal for cross-tenant data isolation gaps.',
    'Rollout multi-language portal (en/es/fr/de) — what changes?',
    'Walk a losing supplier through requesting a debrief.',
  ],
  ss416: [
    'Score this ITT draft for brand-bias and sub-minimum response periods.',
    'Map a $250K EU services deal to the right threshold + portal.',
    'Generate a TED eForms XML payload for this event.',
  ],
  // ── Category & Spend ──────────────────────────────────────
  scm1: [
    'Classify this PO line under the right UNSPSC code.',
    'Find duplicate or orphan codes in my taxonomy.',
    'Migrate from custom L1–L5 to eClass — what changes?',
  ],
  scm2: [
    'Top 3 categories driving spend concentration risk.',
    'Quantify maverick spend leakage this quarter.',
    'Compare actual vs forecast spend by cost-centre.',
  ],
  scm3: [
    'Place this category in the Kraljic matrix and explain why.',
    'Switch a Leverage category to Strategic — what changes?',
    'Recommend review cadence for Bottleneck items.',
  ],
  scm4: [
    'Draft a 12-month category strategy for IT-infrastructure.',
    'Align this category with our cost-out target.',
    'Recommend KPIs that survive an audit.',
  ],
  scm5: [
    'What is the steel commodity-price outlook for Q3?',
    'Surface M&A activity in our top 10 suppliers.',
    'Should we buy now or wait — give me the rationale.',
  ],
  scm6: [
    'Forecast next-quarter demand using usage history.',
    'What safety-stock level survives a 30-day lead-time spike?',
    'Convert demand forecast into a PR-creation timeline.',
  ],
  // ── Supplier Management ───────────────────────────────────
  srm1: [
    'Run sanctions screening on this new supplier.',
    'Auto-classify this supplier by category.',
    'Walk a supplier through completing tax + banking sections.',
  ],
  srm2: [
    'Score this prequalification questionnaire.',
    'Flag red flags in this supplier financial statement.',
    'Recommend approve, conditional, or reject — with rationale.',
  ],
  srm3: [
    'Identify suppliers with declining OTIF / quality.',
    'Draft an improvement plan for an underperforming vendor.',
    'Predict churn risk for our top 20 suppliers.',
  ],
  srm4: [
    'Plan a QBR agenda for a strategic supplier.',
    'Draft a joint innovation roadmap.',
    'Match suppliers to executive sponsors.',
  ],
  srm5: [
    'Score this supplier for combined financial + cyber + geo risk.',
    'Predict failure probability with Altman-Z.',
    'Recommend mitigations — dual-source vs contractual.',
  ],
  srm6: [
    'Profile master data for completeness and freshness.',
    'Auto-merge these 3 suspected-duplicate supplier records.',
    'Surface stale records older than 18 months.',
  ],
  srm7: [
    'Show MBE / WBE certification expiry calendar.',
    'Compare diverse-supplier spend vs our 12 % target.',
    'Recommend diverse candidates for the upcoming RFP.',
  ],
  srm8: [
    'Where is my invoice for PO-12345?',
    'How do I submit an ASN through the portal?',
    'Explain the portal SLA for document acknowledgement.',
  ],
  rq5: [
    'Create a PR for 50 pairs of steel-toe safety boots for Site B by end of July.',
    'Why is my requisition stuck at policy violation?',
    'Convert PR-2026-000123 to a PO against the active framework.',
  ],
  // ── Purchase Orders ───────────────────────────────────────
  po61: [
    'Which of my open POs are overdue on supplier acknowledgement?',
    'Top 3 exceptions I should clear from the workbench today.',
    'Recommend a saved view for IN_PROGRESS POs with no GRN.',
  ],
  po62: [
    'Should I consolidate these 4 PRs for SafetyGear Co into one PO?',
    'Pick the right Incoterms and payment terms for a ₹50L PPE order.',
    'Justify a direct award PO citing FAR 6.302 sole-source exemption.',
  ],
  po63: [
    'Who is the current approver and when does the SLA expire?',
    'Why was buyer-release required after approval here?',
    'What edits to this PO will trigger DOA re-approval?',
  ],
  po64: [
    'Compute the Δ% impact if I raise line 2 quantity by 30%.',
    'Will this change order cross the framework cap?',
    'Draft a defensible reason for a price increase change order.',
  ],
  po65: [
    'Why is line 3 flagged PRICE_MISMATCH — show me the variance.',
    'Recommend the tolerance threshold for this category.',
    'Propose the cleanest resolution for an OVER_INVOICED PO.',
  ],
  po66: [
    'Forecast supplier ack-SLA risk for the next 24 hours.',
    'Draft a counter-proposal response to a delivery-date push.',
    'Which channel — EMAIL, PORTAL or EDI — for this supplier?',
  ],
  po67: [
    'Quantify maverick leakage this quarter by category.',
    'Attribute split-purchase flags to buyer and cost-centre.',
    'Recommend remediation for an off-contract PO.',
  ],
  po68: [
    'Which cycle-time stage is the slowest and why?',
    'A 1-day cut at Approve → Release converts to ₹X — show me.',
    'Build a supplier ack-timeliness scorecard for the last 90 days.',
  ],
  // ── Goods Receipt ─────────────────────────────────────────
  gr71: [
    'Which open PO lines are overdue and need dock-door slots today?',
    'Is any plant × date over the 85% utilisation alert threshold?',
    'Forecast inbound volume for the next 14 days by plant.',
  ],
  gr72: [
    'I have a PR-converted PO for 50 pairs of S3 boots — walk me through capture.',
    'Supplier delivered 110 units against a 100-unit PO — should I accept the excess?',
    'How do I raise a non-PO receipt for an emergency replacement?',
  ],
  gr73: [
    'Which of the 6 inspection rules fired on this GRN?',
    'Lot 500 at AQL 2.5 Level II — what is the sample size and Ac/Re?',
    'A certificate of analysis is missing — can I still ACCEPT?',
  ],
  gr74: [
    'Draft a short-shipment claim with evidence references.',
    'Walk me through the RMA ship-back tracking for a quality rejection.',
    'Build a damage-claim package — what photos do I need?',
  ],
  gr75: [
    'Decode this GRN GL journal — what does each line mean?',
    'When does the GR/IR clearing account get cleared?',
    'CAPEX receipt — does it hit inventory or Capital WIP?',
  ],
  gr76: [
    'Create a milestone-based SES at 60% complete with 10% retention.',
    'My cumulative SES value will exceed PO line value — what happens?',
    'Walk me through dual approval — site engineer then buyer.',
  ],
  gr77: [
    'Run the anomaly scan and explain the top 3 findings.',
    'Which supplier is trending worst on defect ppm over the last quarter?',
    'Show me the OTIF leaderboard and the worst stage to attack.',
  ],
  // iter-110 — GR sub-tab specialists
  gr711: [
    'Parse this EDI 856 payload and tell me which segments are mis-mapped.',
    'Why did my ASN intake fail with "BSN missing"?',
    'Map an EDI 856 line to our canonical ASN schema field-by-field.',
  ],
  gr712: [
    'Sequence today\'s inbound across docks A, B, C — minimise dwell time.',
    'A truck just no-showed at slot 10:00 — who do I reassign?',
    'Estimate the dock utilisation impact if we add a 50-truck spike Thursday.',
  ],
  gr722: [
    'My barcode scan returned "no match" — what should I do?',
    'GPS accuracy is showing ±50m — is that good enough for receiving proof?',
    'I lost network mid-capture — confirm my receipt is queued idempotently.',
  ],
  gr731: [
    'Lot size 1200, AQL 1.0 — give me n, Ac, Re.',
    'Should I switch this supplier from NORMAL to TIGHTENED inspection?',
    'Why is critical-defect zero-acceptance non-negotiable?',
  ],
  gr741: [
    'Pick the cheapest carrier for a 200-kg RMA back to a supplier in Pune.',
    'When can I auto-issue the supplier debit-note for an open RMA?',
    'Set up destruction certification for an RMA with disposition DESTROY.',
  ],
  gr771: [
    'Why is supplier S-001 below 90% OTIF this quarter?',
    'What is the on-time vs in-full split for my bottom 5 suppliers?',
    'Draft a 30/60/90-day OTIF improvement plan for the worst supplier.',
  ],
};
